import "antd/dist/reset.css";

import "./parse.config";

import { init, produce, StorageAdapterLS } from "@opendash/core";
import { getCurrentLanguageSync, resolveTranslation } from "@opendash/i18n";
import { registerIconPack } from "@opendash/icons";
import { HighchartsPlugin } from "@opendash/plugin-highcharts";
import { $monitoring, MonitoringPlugin } from "@opendash/plugin-monitoring";
import { FeedbackPlugin } from "@opendash/plugin-feedback";
import { OpenwarePlugin } from "@opendash/plugin-openware";
import { $parse, ParsePlugin } from "@opendash/plugin-parse";
import { ParseMonitoringPlugin } from "@opendash/plugin-parse-monitoring";
import { TimeseriesPlugin } from "@opendash/plugin-timeseries";
import KauflandRoute from "./components/kaufland";
import AJKRoute from "./components/ajk";
import TransponderAdminRoute from "./components/transponders";
import RuntimeMonitorRoute from "./components/runtimemonitor";
import ManualInputRoute from "./components/manualinput";
import ServiceCurrentRoute from "./components/service/GreisServiceCurrent";
import ServiceViewRoute from "./components/service/GreisServiceView";
import { GroupMailFormat } from "./components/alarms/GroupMailFormat";

import { Parse } from "@openinc/parse-hooks";
import OverviewWidget from "./widgets/overview";
import TableAllWidget from "./widgets/table_all";
import TableSumWidget from "./widgets/table_sum";
import TableSPSWidget from "./widgets/table_sps";
import HGKPIWidget from "./widgets/hg_kpi";
import dayjs from "dayjs";

init("opendash", async (factory) => {
  // @ts-ignore
  registerIconPack(await import("@opendash/icons/dist/fa-regular.json"));
  factory.ui.setLogoImage("/hg.jpg");

  // Translations: Register languages FIRST
  factory.registerLanguage("en", "English");
  factory.registerLanguage("de", "Deutsch", "en", true);
  factory.registerLanguage("nl", "Niederländisch");

  // Register translation resolvers BEFORE plugins to avoid race conditions
  factory.registerTranslationResolver(
    "de",
    "parse-custom",
    async () => (await import("./translations/de.json")).default,
  );

  factory.registerTranslationResolver(
    "en",
    "parse-custom",
    async () => (await import("./translations/en.json")).default,
  );

  factory.registerTranslationResolver(
    "nl",
    "parse-custom",
    async () => (await import("./translations/nl.json")).default,
  );

  factory.registerTranslationResolver(
    "de",
    "greis",
    async () => (await import("./translations/greis/de.json")).default,
  );

  factory.registerTranslationResolver(
    "en",
    "greis",
    async () => (await import("./translations/greis/en.json")).default,
  );

  factory.registerTranslationResolver(
    "nl",
    "greis",
    async () => (await import("./translations/greis/nl.json")).default,
  );

  // Preload translations for the current language to avoid race conditions
  const currentLang = getCurrentLanguageSync();
  await Promise.all([
    resolveTranslation("parse-custom", currentLang),
    resolveTranslation("greis", currentLang),
  ]);

  // Set dayjs locale after language is registered
  dayjs.locale(currentLang);
  factory.ui.disableNotificationHeaderSettings();

  factory.ui.setHeaderSourcePickerProps({
    additionalInfoInSourcePicker: true,
    additionalInfoField: "machineNumber",
    additionalInfoFallbackField: "tag",
    additionalInfoFieldFilter: "tenant-",
  });

  factory.registerAntDesignTranslation(
    "en",
    () => import("antd/lib/locale/en_US"),
  );

  factory.registerAntDesignTranslation(
    "de",
    () => import("antd/lib/locale/de_DE"),
  );

  factory.registerAntDesignTranslation(
    "nl",
    () => import("antd/lib/locale/nl_NL"),
  );

  // Adapter + Plugins

  factory.registerDeviceStorageAdapter(new StorageAdapterLS());

  await factory.use(new TimeseriesPlugin());
  await factory.use(
    new MonitoringPlugin({
      autoCreateInitialDashboard: true,
      autoCreateWidgetOptions: {
        autoCreateWidgets: true,
        autoCreateWidgetsForSource: true,
        autoCreateWidgetsEveryTime: false,
        autoCreateWidgetsEveryTimeForSource: true,
        autoCreateWidgetsContent: "dimension",
        autoCreateWidgetsContentForSource: "source",
        autoCreateWidgetsType: "opendash-widget-hg-kpi",
        autoCreateWidgetsForSourceType: "opendash-widget-hgoverview",
        limitAutoCreateWidgets: 100,
      },
      widgetSorting: {
        enabled: true,
        valueTypeNames: ["Filling", "filling", "llstand", "Containerniveau"],
        autoSortOnFirstRender: {
          enabled: true,
          sourceTagIncludes: ["tenant-"],
        },
      },
      disableWidgets: {
        TimeControlWidget: true,
        NavigationWidget: false,
      },
    }),
  );

  await factory.use(
    new ParsePlugin({
      createAccount: false,
      authLdapActive: false,
      useLiveQueries: false,
      disableNavigationWidgetPreset: true,
      disableNavigationNotificationSettings: true,
      disableNavigationMailSettings: true,
      enableAlarmManagementAndGroups: true,
    }),
  );
  await factory.use(
    new ParseMonitoringPlugin({
      liveQueries: false,
    }),
  );

  await factory.use(
    new OpenwarePlugin({
      secure: true,
      host: "schwarz.henrichgruppe.com",
      //host: "hgneu.openinc.de",
      filterValuesOlderThanMS: 15742800000,
      disableFeature: {
        menu: {
          DataMeta: true,
          DataHierachies: true,
        },
        widgets: {
          owActuators: true,
          sankey: true,
          sunburst: true,
          tableAggregateWidget: true,
        },
        slideshow: true,
        dataCollection: false,
        VKPI: true,
        reporting: false,
        reportingFeatures: {
          adhoc: false,
          schedule: false,
          img: true,
          export: false,
        },
      },
    }),
  );

  await factory.use(
    new HighchartsPlugin({
      disableWidgets: {
        Dependency: true,
        TimeSeriesCompareWidget: true,
        XYCompare: true,
        TableWidget: false,
        TableEnergyStep: true,
        KPIMappedWidget: true,
        HCPivotOI: true,
        PivotSunburst: true,
        KPIWidget: false,
        KPICanvasWidget: false,
        MultiKPIWidget: false,
        KPIMinMaxWidget: false,
        PatternWidget: false,
        PatternHeatmapWidget: false,
        PieWidget: false,
        StatesWidget: false,
        StatesGanttWidget: false,
        TimeSeriesWidget: false,
        PivotTimeseries: false,
      },
    }),
  );

  addcss(`
  :root {
    --od-frontpage-tile-width: 110px;
    --od-frontpage-tile-height: 110px;
    --od-frontpage-tile-width-desktop: 185px;
    --od-frontpage-tile-height-desktop: 165px;
    --od-frontpage-tile-margin: 5px;
  }
  .ant-modal-close-x {margin-top:16}
  .ant-steps-item-icon span {line-height:32px!important}
  .ant-steps-item-icon svg {margin-top:7}
  .ant-drawer-body .data-item-boolean {margin-top:12} 
  .ant-drawer-body .data-item-percentage {margin-top:10}
  .leaflet-top {z-index:998!important} 
  body {margin:0 !important}
  `);

  factory.registerStaticNavigationGroup({
    label: "openware:reporting.label",
    order: 30,
    id: "admin/reporting",
  });

  //Navigation
  factory.registerRoute({
    path: "/hg/transponders/*",
    component: async () => ({ default: TransponderAdminRoute }),
  });

  factory.registerRoute({
    path: "/hg/runtime-monitor/*",
    component: async () => ({ default: RuntimeMonitorRoute }),
  });

  factory.registerRoute({
    path: "/hg/manual-input/*",
    component: async () => ({ default: ManualInputRoute }),
  });

  factory.registerRoute({
    path: "/hg/service-view/current/*",
    headerSourcePicker: false,
    component: async () => ({ default: ServiceCurrentRoute }),
  });

  factory.registerRoute({
    path: "/hg/service-view/all/*",
    headerSourcePicker: false,
    component: async () => ({ default: ServiceViewRoute }),
  });

  factory.registerRoute({
    path: "/hg/kaufland/*",
    headerSourcePicker: false,
    component: async () => ({ default: KauflandRoute }),
  });

  factory.registerRoute({
    path: "/hg/ajk/*",
    headerSourcePicker: false,
    component: async () => ({ default: AJKRoute }),
  });

  factory.registerStaticNavigationItem({
    id: "hg_tranponders",
    group: "hg/transponders",
    place: "frontpage",
    order: 10,
    label: "greis:frontpage.transponder",
    icon: "fa:credit-card",
    color: "#fec000",
    link: "/hg/transponders",
    event: "string",
    permission: "hg:transponders",
    routeCondition: "**",
    activeCondition: "/",
  });

  factory.registerStaticNavigationItem({
    id: "hg_manualinput",
    group: "hg/manualinput",
    place: "frontpage",
    order: 10,
    label: "greis:frontpage.manual_input",
    icon: "fa:keyboard",
    color: "#fec000",
    link: "/hg/manual-input",
    event: "string",
    permission: "hg:manual-input",
    routeCondition: "**",
    activeCondition: "/",
  });

  //Service
  factory.registerStaticNavigationGroup({
    id: "hg_serviceview",
    label: "greis:frontpage.service_view_group",
    icon: "fa:tools",
    order: 50,
  });

  factory.registerStaticNavigationItem({
    id: "hg_serviceview",
    group: "hg/serviceview",
    place: "frontpage",
    order: 10,
    label: "greis:frontpage.service_view",
    icon: "fa:tools",
    color: "#fec000",
    link: "/hg/service-view/current",
    event: "string",
    permission: "hg:service-view",
    routeCondition: "**",
    activeCondition: "/",
  });

  factory.registerStaticNavigationItem({
    id: "greis_service_current",
    group: "hg_serviceview",
    place: "sidebar",
    order: 10,
    label: "greis:serviceview.menu_current",
    icon: "fa:clock",
    link: "/hg/service-view/current",
    permission: "hg:service-view",
    routeCondition: ["/hg/service-view/**", "/hg/runtime-monitor/**"],
    activeCondition: "/hg/service-view/current",
  });

  factory.registerStaticNavigationItem({
    id: "greis_service_all",
    group: "hg_serviceview",
    place: "sidebar",
    order: 20,
    label: "greis:serviceview.menu_all",
    icon: "fa:table",
    link: "/hg/service-view/all",
    permission: "hg:service-view",
    routeCondition: ["/hg/service-view/**", "/hg/runtime-monitor/**"],
    activeCondition: "/hg/service-view/all",
  });

  factory.registerStaticNavigationItem({
    id: "greis_service_runtime",
    group: "hg_serviceview",
    place: "sidebar",
    order: 30,
    label: "greis:frontpage.runtime_monitor",
    icon: "fa:bell",
    link: "/hg/runtime-monitor",
    permission: "hg:service-view",
    routeCondition: ["/hg/service-view/**", "/hg/runtime-monitor/**"],
    activeCondition: "/hg/runtime-monitor",
  });

  //Kaufland/Lidl
  /*
  factory.registerStaticNavigationItem({
    id: "hg_kaufland",
    group: "hg/kaufland",
    place: "frontpage",
    order: 10,
    label: "Kaufland/Lidl",
    icon: "fa:store",
    color: "#fec000",
    link: "/hg/kaufland",
    event: "string",
    permission: "hg:kaufland-view",
    routeCondition: "**",
    activeCondition: "/",
  });*/

  //AJK
  /*
  factory.registerStaticNavigationItem({
    id: "hg_ajk",
    group: "hg/ajk",
    place: "frontpage",
    order: 10,
    label: "AJK",
    icon: "fa:store",
    color: "#fec000",
    link: "/hg/ajk",
    event: "string",
    permission: "hg:ajk-view",
    routeCondition: "**",
    activeCondition: "/",
  });*/

  //Class Config
  $parse.ui.setClassConfig({
    className: "HG_Kaufland",
    titleFields: ["machine"],
    displayFields: ["machine", "store_no", "order_code"],
    createFields: ["machine", "store_no", "order_code"],
    editFields: ["machine", "store_no", "order_code"],
  });

  $parse.ui.setClassConfig({
    className: "HG_Lidl",
    titleFields: ["machine"],
    displayFields: ["machine", "store_no", "lidl_company_no", "machine_secret"],
    createFields: ["machine", "store_no", "lidl_company_no", "machine_secret"],
    editFields: ["machine", "store_no", "lidl_company_no", "machine_secret"],
  });

  $parse.ui.setClassConfig({
    className: "HG_AJK",
    titleFields: ["machine"],
    displayFields: ["machine", "ajk_number", "hg_number"],
    createFields: ["machine", "ajk_number", "hg_number"],
    editFields: ["machine", "ajk_number", "hg_number"],
    defaultACL: () => ({
      "role:od-admin": { read: true, write: true },
    }),
    beforeSave: async (object: Parse.Object) => {
      const machine = object.get("machine");
      if (machine) {
        const fetchedMachine = await machine.fetch();
        const machineACL = fetchedMachine.getACL();
        if (machineACL) {
          object.setACL(machineACL);
        }
      }
    },
  });

  $parse.ui.setClassConfig({
    className: "OD3_WidgetPreset",
    titleFields: ["label"],
    displayFields: ["label", "tags", "description"],
    createFields: [],
    editFields: ["label", "tags", "description"],
  });

  $parse.ui.setClassConfig({
    className: "_User",
    titleFields: ["name"],
    displayFields: ["name", "tenant", "email", "updatedAt"],
    createFields: ["name", "email", "tenant", "password"],
    editFields: ["name", "email", "tenant", "password"],
    beforeSave: (object) => {
      if (object.get("email")) {
        object.set("username", object.get("email"));
      }
    },
  });
  /*
  $parse.ui.setDefaultView("OD3_Mail_Groups", {
    type: "table",
    showSearch: true,
  });
  $parse.ui.setClassConfig({
    className: "OD3_Mail_Groups",
    titleFields: ["Name"],
    displayFields: ["Name", "Mails", "user"],
    createFields: ["Name", "Mails"],
    editFields: ["Mails"],
    customFields: {
      Mails: GroupMailFormat,
    },
    defaultACL: () => {
      if (!$parse.user.id()) {
        return {};
      }
      const userId = $parse.user.id() || "";
      const acl: Record<string, { read: boolean; write: boolean }> = {};
      acl[userId] = { read: true, write: true };
      acl["role:od-admin"] = {
        read: true,
        write: true,
      };

      return acl;
    },
  });
*/
  $parse.ui.setDefaultView("OD3_WidgetPreset", {
    type: "table",
  });
  $parse.ui.setClassConfig({
    className: "OD3_Source",
    titleFields: ["name"],
    displayFields: [
      "name",
      "machineNumber",
      "address",
      "tag",
      "type",
      "price",
      "weighing",
      "parent",
      "tenant",
    ],
    createFields: [
      "name",
      "machineNumber",
      "address",
      "tag",
      "type",
      "price",
      "weighing",
      "parent",
      "tenant",
    ],
    editFields: [
      "name",
      "machineNumber",
      "address",
      "type",
      "price",
      "weighing",
      "parent",
      "tenant",
    ],
    beforeSave: (object) => {
      // add TenantUser to ACL
      if (object.has("tenant") && object.get("tenant")) {
        const tenant = object.get("tenant").id;
        const acl: Record<string, { read: boolean; write: boolean }> = {};
        acl[`role:od-tenant-user-${tenant}`] = { read: true, write: true };
        object.setACL(new Parse.ACL(acl));
      }
    },
  });

  $parse.ui.setClassConfig({
    className: "OD3_Tenant",
    titleFields: ["label"],
    displayFields: ["label"],
    createFields: ["label"],
    editFields: ["label"],
  });

  $parse.ui.setClassConfig({
    className: "OD3_Permission",
    titleFields: ["label"],
    displayFields: ["label", "key"],
    createFields: ["label", "key"],
    editFields: ["label", "key"],
  });

  $parse.ui.setClassConfig({
    className: "OD3_Dashboard",
    titleFields: ["name"],
    displayFields: ["name", "source"],
    createFields: ["name"],
    editFields: ["name"],
  });

  $parse.ui.setDefaultView("OD3_Source", {
    type: "table",
    showSearch: true,
  });

  $parse.ui.setDefaultView("OD3_Tenant", {
    type: "table",
    showSearch: true,
  });

  $parse.ui.setClassConfig({
    className: "OD3_Alarm",
    titleFields: ["user"],
    displayFields: [
      "user",
      "condition",
      "trigger",
      "action",
      "createdAt",
      "updatedAt",
    ],
    createFields: ["user", "condition", "trigger", "action"],
    editFields: ["user", "condition", "trigger", "action"],
  });

  $parse.ui.setDefaultView("OD3_Alarm", {
    type: "table",
  });

  $parse.ui.setClassConfig({
    className: "_Role",
    titleFields: ["label"],
    displayFields: ["label", "name", "users", "createdAt", "updatedAt"],
    createFields: ["label", "name"],
    editFields: ["label", "name", "users"],
    defaultACL: () => {
      if (!$parse.user.id()) {
        return {};
      }

      const userId = $parse.user.id() || "";
      return {
        [userId]: {
          read: true,
          write: true,
        },
      };
    },
  });
  $parse.ui.setDefaultView("_Role", {
    type: "table",
    showSearch: true,
  });

  $parse.ui.setDefaultView("_User", {
    type: "table",
    showSearch: true,
  });

  $monitoring.registerWidget(OverviewWidget);
  $monitoring.registerWidget(TableAllWidget);
  $monitoring.registerWidget(TableSumWidget);
  $monitoring.registerWidget(TableSPSWidget);
  $monitoring.registerWidget(HGKPIWidget);
}).then((app) => {
  console.log("init open.DASH");
});
//@ts-expect-error
function addcss(css) {
  const head = document.getElementsByTagName("head")[0];
  const s = document.createElement("style");
  s.setAttribute("type", "text/css");
  s.appendChild(document.createTextNode(css));
  head.appendChild(s);
}
